import React, { useEffect, useMemo, useRef, useState } from "react";
import PageShell from "../components/PageShell";
import "./AdminBranding.css";
import { useBranding } from "./BrandingContext";

// Cache-bust helper so logo/preview refreshes even when URL is stable
const withCacheBust = (url, token) => {
  if (!url) return url;
  const u = String(url);
  const sep = u.includes("?") ? "&" : "?";
  return `${u}${sep}v=${token}`;
};

const normalizeHex = (hex) => {
  if (!hex) return "";
  const raw = String(hex).trim();
  if (!raw) return "";
  const h = raw.startsWith("#") ? raw.slice(1) : raw;
  if (![3, 6].includes(h.length)) return raw;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return `#${full.toLowerCase()}`;
};

const AdminBranding = () => {
  const { refreshBranding } = useBranding() || {};

  const [branding, setBranding] = useState({
    logo_url: "",
    primary_color: "#5b5ce2",
    secondary_color: "#111827",
    tone_preset: "professional",
  });

  const [previewHtml, setPreviewHtml] = useState("");
  const [previewKey, setPreviewKey] = useState(0);
  const [logoUploading, setLogoUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [logoCacheBust, setLogoCacheBust] = useState(Date.now());

  // Debounce email preview rendering while editing form values.
  const previewDebounceRef = useRef(null);

  const brandingForPreview = useMemo(
    () => ({
      ...branding,
      primary_color: normalizeHex(branding.primary_color),
      secondary_color: normalizeHex(branding.secondary_color),
    }),
    [branding],
  );

  useEffect(() => {
    // Load stored branding once
    fetch("/api/company/branding", { credentials: "include", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (data) {
          setBranding((prev) => ({ ...prev, ...data }));
          if (data.logo_url) setLogoCacheBust(Date.now());
        }
      })
      .catch(() => setError("Unable to load branding settings."))
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadPreview = async (overrideBranding = null) => {
    try {
      const method = overrideBranding ? "POST" : "GET";
      const res = await fetch("/api/company/email-preview", {
        method,
        credentials: "include",
        cache: "no-store",
        headers: overrideBranding ? { "Content-Type": "application/json" } : undefined,
        body: overrideBranding ? JSON.stringify({ branding: overrideBranding }) : undefined,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPreviewHtml(data?.html || "");
      setPreviewKey(Date.now()); // force iframe remount
    } catch {
      // non-blocking
    }
  };

  // Auto-refresh preview as the admin edits fields (without saving).
  useEffect(() => {
    if (loading) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(() => {
      loadPreview(brandingForPreview);
    }, 250);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingForPreview, loading]);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("/api/company/logo", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error();

      const data = await res.json();
      const newLogo = data?.logo_url || branding.logo_url;

      // Update logo and bust cache for immediate visual refresh.
      setLogoCacheBust(Date.now());

      setBranding((prev) => ({
        ...prev,
        logo_url: newLogo,
        // Server extracts colors for PNG/JPG on upload. Prefer those.
        primary_color: data?.primary_color || prev.primary_color,
        secondary_color: data?.secondary_color || prev.secondary_color,
      }));

      // Refresh stored branding (source of truth)
      await refreshBranding?.();
      await loadPreview();
    } catch {
      setError("Logo upload failed. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleChange = (key, value) => {
    setBranding((prev) => ({ ...prev, [key]: value }));
  };

  const saveBranding = async (payload) => {
    setSaving(true);
    setError("");

    try {
      const res = await fetch("/api/company/branding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      await refreshBranding?.();
      await loadPreview();
    } catch {
      setError("Unable to save branding settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await saveBranding(brandingForPreview);
  };

  const handleResetToLogoColors = async () => {
    setError("");
    if (!branding.logo_url) {
      setError("Upload a logo first to auto-detect brand colors.");
      return;
    }

    try {
      const res = await fetch("/api/company/extract-logo-colors", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => null);
        throw new Error(msg?.error || "extract-failed");
      }
      const data = await res.json();
      const next = {
        ...branding,
        primary_color: data?.primary_color || branding.primary_color,
        secondary_color: data?.secondary_color || branding.secondary_color,
      };
      setBranding(next);
      await refreshBranding?.();
      await loadPreview();
    } catch (e) {
      const text = String(e?.message || "");
      if (text.toLowerCase().includes("svg")) {
        setError("Automatic color detection does not support SVG logos. Please upload a PNG or JPG.");
      } else {
        setError("Unable to detect colors from the current logo. Please upload a PNG/JPG logo and try again.");
      }
    }
  };

  return (
    <PageShell title="Branding" subtitle="Configure logo, colors, and tone for outgoing emails.">
      {error ? <div className="pill brandAlert brandAlert--error">{error}</div> : null}

      {loading ? (
        <div className="card">
          <div className="card__body brandEmpty">Loading branding settings…</div>
        </div>
      ) : (
        <div className="grid-2 brandGrid">
          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Company email branding</h2>
              <p className="card__subtitle">These settings apply only to outgoing emails.</p>
            </div>

            <div className="card__body">
              <div className="brandForm">
                <div className="brandField brandSpan2">
                  <label className="label">Logo</label>
                  <div className="brandLogoRow">
                    <div className="brandLogoPreview">
                      {branding.logo_url ? (
                        <img src={withCacheBust(branding.logo_url, logoCacheBust)} alt="Company logo" />
                      ) : (
                        <div className="brandLogoPlaceholder">No logo uploaded</div>
                      )}
                    </div>

                    <div className="brandLogoActions">
                      <label className="btn btn--secondary" htmlFor="brand-logo-input">
                        {logoUploading ? "Uploading…" : "Upload logo"}
                      </label>
                      <input
                        id="brand-logo-input"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                        disabled={logoUploading}
                      />
                      <div className="help">PNG/JPG recommended for automatic color detection.</div>
                    </div>
                  </div>
                </div>

                <div className="brandField">
                  <label className="label">Primary color</label>
                  <div className="brandColorRow">
                    <input
                      className="input brandColorInput"
                      type="color"
                      value={brandingForPreview.primary_color || "#5b5ce2"}
                      onChange={(e) => handleChange("primary_color", e.target.value)}
                      data-testid="branding-primary-color-picker"
                    />
                    <input
                      className="input"
                      value={brandingForPreview.primary_color || ""}
                      onChange={(e) => handleChange("primary_color", e.target.value)}
                      data-testid="branding-primary-color"
                    />
                  </div>
                </div>

                <div className="brandField">
                  <label className="label">Secondary color</label>
                  <div className="brandColorRow">
                    <input
                      className="input brandColorInput"
                      type="color"
                      value={brandingForPreview.secondary_color || "#111827"}
                      onChange={(e) => handleChange("secondary_color", e.target.value)}
                      data-testid="branding-secondary-color-picker"
                    />
                    <input
                      className="input"
                      value={brandingForPreview.secondary_color || ""}
                      onChange={(e) => handleChange("secondary_color", e.target.value)}
                      data-testid="branding-secondary-color"
                    />
                  </div>
                </div>

                <div className="brandField brandSpan2">
                  <label className="label">Tone preset</label>
                  <select
                    className="select"
                    value={brandingForPreview.tone_preset || "professional"}
                    onChange={(e) => handleChange("tone_preset", e.target.value)}
                    data-testid="branding-tone"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="celebratory">Celebratory</option>
                    <option value="formal">Formal</option>
                  </select>
                  <div className="help">Used as the default writing style when generating messages.</div>
                </div>
              </div>

              <div className="brandActions">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="branding-save"
                >
                  {saving ? "Saving…" : "Save branding"}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleResetToLogoColors}
                  disabled={saving || logoUploading}
                  title="Detect primary/secondary colors from the uploaded logo"
                >
                  Detect colors from logo
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => loadPreview()} disabled={saving}>
                  Refresh email preview
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Email preview</h2>
              <p className="card__subtitle">A live sample rendered with your current settings.</p>
            </div>
            <div className="card__body">
              <div className="brandPreviewFrame">
                {previewHtml ? (
                  <iframe key={previewKey} title="Email preview" srcDoc={previewHtml} />
                ) : (
                  <div className="brandEmpty">Preview unavailable.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default AdminBranding;
